import classnames from "classnames";
import React, { useEffect, useRef } from "react";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { gettext } from "web/script/modules/django-i18n";
import { AllSelectedFilterInfo, StandardFilterProps } from "web/types/feed-filters";
import { UniversalFiltersData } from "web/types/universal-filters";
import BreadcrumbPills from "./feeds-filter-breadcrumb-pills/feeds-filter-breadcrumb-pills";
import FilterPagination from "./feeds-filters-pagination/filter-pagination";
import {
    PaginatedItem,
    PaginationExpansionState,
    PaginationProps,
} from "./feeds-filters-pagination/pagination-state";
import styles from "./feeds-filters.css";
import { useFilterScrollPosition } from "./hooks/use-filter-scroll-position";

interface FeedFiltersProps extends PaginationProps, StandardFilterProps {
    applyState(): void;
    canApply: boolean;
    displayedFilters: UniversalFiltersData;
    isLoading: boolean;
    isOpen: boolean;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    onClose: (item: PaginatedItem) => void;
    selectedFilterInfo: AllSelectedFilterInfo;
    clearAllFilters(): void;
}

function FeedFilters({
    applyState,
    canApply,
    displayedFilters,
    isLoading,
    isOpen,
    lazyFacetFetch,
    lazyFacetIsLoading,
    onClick,
    onClose,
    onPageChange,
    paginationState,
    pendingFilterInfo,
    selectedFilterInfo,
    clearAllFilters,
}: FeedFiltersProps): JSX.Element {
    let scrollAreaRef = useRef<HTMLDivElement>(null);
    let pillContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollAreaRef.current) {
            return;
        }

        if (scrollAreaRef.current.scrollTop != 0 && isOpen) {
            scrollAreaRef.current.scrollTop = 0;
        }
    }, [isOpen]);

    const { containerRef, onItemExpanded } = useFilterScrollPosition(
        scrollAreaRef,
        paginationState,
        isOpen
    );

    function handlePageChange(item: PaginatedItem, order?: number): void {
        let itemExpansionState = paginationState.getExpansionState(item);
        if (itemExpansionState != PaginationExpansionState.Collapsed) {
            onClose(item);
            return;
        } else if (item.type == "category" && !pendingFilterInfo.getChecked(item)) {
            onClick(item);
        }

        if (isOpen && typeof order != "undefined") {
            onItemExpanded(order);
        }

        onPageChange(item);
    }

    return (
        <div
            className={classnames(styles.container, {
                [styles.loading]: isLoading,
            })}
        >
            <div className={styles.content} ref={scrollAreaRef}>
                <div className={styles.sticky}>
                    <div className={styles.filterHeader}>
                        <h3 className={styles.title}>{gettext("general.filters")}</h3>
                        <button className={styles.clearAll} onClick={clearAllFilters}>
                            {gettext("feed.filters.mobile_clear_all_btn_text")}
                        </button>
                    </div>
                    <div ref={pillContainerRef}>
                        <BreadcrumbPills
                            className={styles.breadcrumbs}
                            onClick={onClick}
                            selectedFilterInfo={selectedFilterInfo}
                        />
                    </div>
                </div>
                <FilterPagination
                    ref={containerRef}
                    allSelectedFilterInfo={selectedFilterInfo}
                    sections={displayedFilters.filter_section_list}
                    onPageChange={handlePageChange}
                    {...{
                        lazyFacetFetch,
                        lazyFacetIsLoading,
                        onClick,
                        paginationState,
                        pendingFilterInfo,
                    }}
                />
            </div>
            <div className={styles.applyButton}>
                <PrimaryButton onClick={applyState} fullWidth={true} disabled={!canApply}>
                    {gettext("feed.filters.apply_btn_text") /* Apply Filters */}
                    {/* // TODO: Just realised we don't have a differences count */}
                </PrimaryButton>
            </div>
            {isLoading && <LoadingSpinner />}
        </div>
    );
}

export default FeedFilters;
